import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import {
  attemptSeed,
  looksLikeOwnRetry,
  noteAnswered,
  noteUnanswered,
  type AttemptLog,
} from './activationAttempt'
import {
  activateVoucher,
  isUnanswered,
  normalizeVoucherCode,
  type ActivationVerdict,
} from './voucherApi'

export interface VoucherActivation {
  /** Serverning ANIQ javobi — sakkiz holatdan biri */
  verdict: ActivationVerdict | null
  /**
   * HAQIQIY nosozlik: tarmoq uzildi, 500, noma'lum `tag`.
   *
   * ⚠️ `verdict` DAN ALOHIDA va bu `useCashOperation` dagi qoidaning
   * takrori: server aniq javob bergan holatda kassir BOSHQA ish
   * qiladi, nosozlikda esa QAYTA URINADI. Ikkalasi bitta qizil banner
   * bo'lsa, kassir ishlatilgan kartani ham qayta-qayta yuborardi.
   */
  failure: string | null
  running: boolean
  /**
   * ⚠️ Natijani QAYTARADI ham. Chaqiruvchiga u holat sifatida emas,
   * QAROR uchun kerak: faqat `ACTIVATED` da maydonlar tozalanadi
   * (keyingi mijoz), qolgan holatlarda kassir kod yoki loginni
   * tuzatadi va `useState` ning yangi qiymati o'sha daqiqada hali
   * ko'rinmaydi. `null` — haqiqiy nosozlik.
   */
  activate: (code: string, login: string) => Promise<ActivationVerdict | null>
  reset: () => void
}

export function useVoucherActivation(): VoucherActivation {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [verdict, setVerdict] = useState<ActivationVerdict | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [running, setRunning] = useState(false)

  /**
   * ⚠️ `useRef`, `useState` EMAS (`ConfirmAction` dagi bilan bir xil
   * sabab): iz o'qiladigan payt — so'rov TUGAGAN daqiqa, ya'ni
   * `setState` ning yangi qiymati hali ko'rinmaydigan joy. Qayta
   * render ham kerak emas.
   */
  const log = useRef<AttemptLog>(null)

  const activate = useCallback(
    async (code: string, login: string): Promise<ActivationVerdict | null> => {
      // ⚠️ Barmoq NORMALLASHTIRILGAN kod bo'yicha: kassir ikkinchi
      // urinishda kodni kichik harfda yoki bo'shliq bilan tersa ham
      // bu AYNI urinish (server ham shunday ko'radi).
      const seed = attemptSeed(normalizeVoucherCode(code), login.trim())
      const retryOfUnanswered = looksLikeOwnRetry(log.current, seed)

      setRunning(true)
      setVerdict(null)
      setFailure(null)
      try {
        const outcome = await activateVoucher(code, login)
        log.current = noteAnswered(log.current, seed, outcome.kind === 'USED')
        const shown: ActivationVerdict =
          outcome.kind === 'USED' && retryOfUnanswered ? { kind: 'OWN_RETRY' } : outcome
        setVerdict(shown)

        /*
          ⚠️ KESH KALITSIZ bekor qilinadi va bu ATAYLAB.

          Faollashtirish uch shoxga ta'sir qiladi — abonentlar
          (`subscribers`), kassa katalogi (`cashdesk`) va sherik
          amallari (`partnerAccount`) — lekin ularning kalit fayllari
          BOSHQA `features/*` da, va `07-FRONTEND.md` §6 ularni bu
          yerdan import qilishni taqiqlaydi. Prefikslarni satr sifatida
          qayta yozish esa aynan o'sha kalit fayllari ogohlantirgan
          nuqson bo'lardi: nom o'zgarganda bekor qilish JIMGINA
          ishlamay qolardi.

          ⚠️ Kalitsiz chaqiruv «hammasini qayta so'rash» DEGANI EMAS:
          TanStack Query standart bo'yicha faqat FAOL so'rovlarni
          yangilaydi, qolganini eskirgan deb belgilaydi. Bu ekranda
          faol so'rov yo'q, ya'ni hozir bitta ham tarmoq so'rovi
          ketmaydi — kassirning telefonida bu bepul.
        */
        void queryClient.invalidateQueries()
        return shown
      } catch (error) {
        log.current = isUnanswered(error)
          ? noteUnanswered(seed)
          : noteAnswered(log.current, seed, false)
        setFailure(translateError(error, t))
        return null
      } finally {
        setRunning(false)
      }
    },
    [queryClient, t],
  )

  const reset = useCallback(() => {
    setVerdict(null)
    setFailure(null)
  }, [])

  return { verdict, failure, running, activate, reset }
}
